import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ListingCard from '@/components/ListingCard';
import AdBanner from '@/components/AdBanner';
import api from '@/lib/api';
import { Search, MapPin, SlidersHorizontal, Loader2, Package } from 'lucide-react';

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [location, setLocation] = useState(searchParams.get('location') || '');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);

  const fetchListings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.set('category', category);
      if (search) params.set('search', search);
      if (location) params.set('location', location);
      params.set('sort', sort);
      params.set('page', page);
      params.set('limit', 12);
      const { data } = await api.get(`/listings?${params}`);
      setListings(data.listings);
      setTotal(data.total);
      setPages(data.pages);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [category, sort, page]);

  useEffect(() => {
    setCategory(searchParams.get('category') || 'all');
    setSearch(searchParams.get('search') || '');
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchListings();
  };

  const categoryLabels = { all: 'All Categories', sale: 'For Sale', free: 'Free', lending: 'Lending' };

  return (
    <div className="min-h-screen" data-testid="browse-page">
      <div className="bg-white border-b border-[#E8E6DF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#2C362F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Browse Listings
          </h1>
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search items..."
                className="pl-10 rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-11"
                data-testid="search-input"
              />
            </div>
            <div className="relative w-full sm:w-48">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" strokeWidth={1.5} />
              <Input
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="Location..."
                className="pl-10 rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-11"
                data-testid="location-filter"
              />
            </div>
            <Button type="submit" className="rounded-xl h-11 bg-primary text-white px-6" data-testid="search-btn">
              <Search className="w-4 h-4 mr-2" strokeWidth={1.5} />Search
            </Button>
          </form>

          <div className="flex flex-wrap items-center gap-2 mt-4">
            {['all', 'sale', 'free', 'lending'].map(cat => (
              <Button
                key={cat}
                variant={category === cat ? 'default' : 'outline'}
                size="sm"
                className={`rounded-full ${category === cat ? 'bg-primary text-white' : 'border-[#E8E6DF] hover:bg-[#F4F1EA]'}`}
                onClick={() => { setCategory(cat); setPage(1); }}
                data-testid={`category-filter-${cat}`}
              >
                {categoryLabels[cat]}
              </Button>
            ))}
            <div className="ml-auto">
              <Select value={sort} onValueChange={v => { setSort(v); setPage(1); }}>
                <SelectTrigger className="w-40 rounded-xl border-[#E8E6DF] h-9" data-testid="sort-select">
                  <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest First</SelectItem>
                  <SelectItem value="oldest">Oldest First</SelectItem>
                  <SelectItem value="price_low">Price: Low to High</SelectItem>
                  <SelectItem value="price_high">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <div className="mb-6">
          <AdBanner placement="category" />
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : listings.length > 0 ? (
          <>
            <p className="text-sm text-muted-foreground mb-4">{total} listing{total !== 1 ? 's' : ''} found</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="listing-grid">
              {listings.map(l => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
            {pages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn">
                  Previous
                </Button>
                <span className="flex items-center text-sm text-muted-foreground px-3">Page {page} of {pages}</span>
                <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn">
                  Next
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-20">
            <img src="https://images.unsplash.com/photo-1617611313649-fbddfaa37b52?w=200&h=200&fit=crop" alt="" className="w-24 h-24 rounded-2xl object-cover mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium text-[#2C362F] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No listings found</p>
            <p className="text-sm text-muted-foreground mb-4">Try adjusting your filters or search terms</p>
            <Link to="/create-listing">
              <Button className="rounded-full bg-primary text-white">Post a Listing</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
